import { createElement } from 'react';
import styled from 'styled-components';
import { ReactComponent as TableIcon } from '@elementary/common-ui/assets/16x16/table.svg';
import { ReactComponent as NoTableIcon } from '@elementary/common-ui/assets/16x16/no-table.svg';
import { ReactComponent as ColumnIcon } from '@elementary/common-ui/assets/columns.svg';
import {
  Flex,
  FlexCenter,
  FlexColumn,
  LabelLink,
  Link,
  OverflowTooltip,
} from '@elementary/common-ui/components';
import { getTestIcon } from '@elementary/common-ui/components/cellRenderers/testNameRenderer';
import { Typography } from '@mui/material';
import useNavigateWithEnvParam from '@elementary/common-ui/hooks/useNavigateWithEnvParam';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import useTreeViewParams from '@elementary/common-ui/hooks/useTreeViewParams';
import CopyButton from 'shared/components/CopyButton';
import {
  TREE_NODE,
  TREE_TYPE,
  TREE_VIEW_BY,
} from '@elementary/common-ui/constants/urlParams';

const Container = styled(FlexColumn)`
  margin-top: 8px;
  gap: 8px;
`;

const NamesRow = styled.div`
  display: grid;
  grid-template-columns: fit-content(50%) 1fr;
  gap: 16px;
  overflow: hidden;
  align-items: center;
`;

const IconText = styled(Flex)`
  align-items: center;
  gap: 8px;
  min-width: 0;
  overflow: hidden;

  > svg {
    path {
      fill: ${({ theme }) => theme.palette.common.gray4};
    }
  }
`;

const IconWrapper = styled(FlexCenter)`
  flex-shrink: 0;
`;

const TestNameWrapper = styled(Flex)`
  overflow: hidden;
  font-size: 14px;
`;

const ResourceNameWrapper = styled(Flex)`
  overflow: hidden;
`;

const StyledCopyButton = styled(CopyButton)`
  height: 20px;
  width: 20px;
  flex-shrink: 0;
`;

const Label = styled(Typography)`
  font-size: 14px;
`;

const Description = styled(Label)`
  color: ${({ theme }) => theme.palette.common.gray2};
`;

const TableIconStyled = styled(TableIcon)`
  width: 17px;
  min-width: 17px;
`;

const ColumnIconStyled = styled(ColumnIcon)`
  width: 17px;
  min-width: 17px;
`;

const NoAssetsText = styled(Typography)`
  color: ${({ theme }) => theme.palette.common.gray4};
  font-size: 14px;
  white-space: nowrap;
`;

type Props = {
  resourceId?: string;
  testName: string;
  testType: string;
  testSubType: string;
  resourceName?: string;
  resourceType?: string;
  description: string;
};

const TestOverviewHeader = ({
  resourceId = '',
  testName,
  testType,
  testSubType,
  resourceName,
  resourceType,
  description,
}: Props) => {
  const { formatUrlParam } = useSelectedTreeItem();
  const { treeType, treeViewBy } = useTreeViewParams();
  const navigate = useNavigateWithEnvParam();
  const newParams = new URLSearchParams();

  const navigateToResource = () => {
    const urlParam = formatUrlParam(resourceId);

    if (resourceId) {
      newParams.set(
        TREE_NODE,
        JSON.stringify({ ...JSON.parse(urlParam || '{}'), id: resourceId }),
      );
      newParams.set(TREE_TYPE, treeType);
      newParams.set(TREE_VIEW_BY, treeViewBy);

      navigate(`/catalog?${newParams?.toString()}`);
    }
  };

  return (
    <Container>
      <NamesRow>
        <IconText>
          <IconWrapper>
            {createElement(getTestIcon(testType, testSubType) || (() => null))}
          </IconWrapper>
          <TestNameWrapper>
            <OverflowTooltip tooltipContent={testName} variant="body2">
              {testName}
            </OverflowTooltip>
          </TestNameWrapper>
        </IconText>

        {resourceName && resourceType ? (
          <IconText>
            <IconWrapper>
              {resourceType === 'COLUMN' ? (
                <ColumnIconStyled />
              ) : (
                <TableIconStyled />
              )}
            </IconWrapper>
            <ResourceNameWrapper>
              <OverflowTooltip tooltipContent={resourceName}>
                {resourceId ? (
                  <LabelLink onClick={navigateToResource}>
                    {resourceName}
                  </LabelLink>
                ) : (
                  <Label>{resourceName}</Label>
                )}
              </OverflowTooltip>
            </ResourceNameWrapper>
            <StyledCopyButton textToCopy={resourceName} />
          </IconText>
        ) : (
          <IconText>
            <IconWrapper>
              <NoTableIcon />
            </IconWrapper>
            <NoAssetsText>
              No assets linked to this test.{' '}
              <Link
                target="_blank"
                href="https://docs.elementary-data.com/cloud/features/data-tests/data-tests-overview"
              >
                Learn more
              </Link>
            </NoAssetsText>
          </IconText>
        )}
      </NamesRow>

      <Description>{description}</Description>
    </Container>
  );
};

export default TestOverviewHeader;
